import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  rounded?: boolean;
  children: ReactNode;
  className?: string;
};

type ButtonAsButton = BaseButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    href?: undefined;
  };

type ButtonAsLink = BaseButtonProps & {
  href: string;
  disabled?: boolean;
  target?: string;
  rel?: string;
};

type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-button-solid-bg text-text-inverse hover:bg-button-solid-bg-hover disabled:bg-disabled-bg',
  secondary:
    'bg-transparent text-button-outline-border hover:bg-button-outline-hover-bg border border-button-outline-border',
  ghost: 'bg-transparent text-button-ghost-text hover:bg-button-outline-hover-bg',
  danger: 'bg-error text-text-inverse hover:bg-error disabled:bg-error-bg',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  rounded = false,
  className,
  children,
  ...props
}: ButtonProps) => {
  const baseStyles = clsx(
    'inline-flex items-center justify-center font-medium transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2',
    'disabled:cursor-not-allowed',
    rounded ? 'rounded-full p-2' : 'rounded-lg',
    !rounded && sizeStyles[size],
    variantStyles[variant],
    className,
  );

  // Link button
  if ('href' in props && props.href !== undefined) {
    const { href, disabled, ...rest } = props;
    if (disabled) {
      return (
        <span className={clsx(baseStyles, 'cursor-not-allowed opacity-50')} {...rest}>
          {children}
        </span>
      );
    }
    return (
      <Link to={href} className={baseStyles} {...rest}>
        {children}
      </Link>
    );
  }

  // Regular button - safe assertion: we've already ruled out ButtonAsLink above
  // eslint-disable-next-line no-unsafe-type-assertion
  const { disabled, ...buttonProps } = props as ButtonAsButton;
  return (
    <button className={baseStyles} disabled={disabled} {...buttonProps}>
      {children}
    </button>
  );
};
